import React, { useState, useEffect } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonLoading, IonToast, IonButton } from '@ionic/react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import { Visibility, CheckCircle, Pending, Warning, Inventory } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { approveBarRequest, approveStewardRequest, getAllBarRequestsWithItems, getPendingBarRequestsWithItems, getPendingStewardRequestsWithItems } from '../../../api/stock-transfer/stock-transfer';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  '&.pending': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  '&.completed': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  '&.partial': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

interface RequestItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  available: number;
  transferred?: number;
}

interface TransferRequest {
  id: string;
  requested_by: string;
  items: RequestItem[];
  status: 'pending' | 'completed' | 'partial';
  date: string;
  notes?: string;
}

const StewardPurchaseOrderRequestPage: React.FC = () => {
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<TransferRequest | null>(null);
  const [transferQuantities, setTransferQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  // const fetchPendingRequests = async () => {
  //   setLoading(true);



  //   try {
  //     // Simulate API call with available quantities
  //     const mockRequests: TransferRequest[] = [
  //       { 
  //         id: 'REQ-001', 
  //         requested_by: 'Main Bar', 
  //         items: [
  //           { 
  //             id: '1-1', 
  //             product_id: '1', 
  //             product_name: 'Vodka', 
  //             quantity: 5,
  //             available: 3 // Only 3 available in warehouse
  //           },
  //           { 
  //             id: '1-2', 
  //             product_id: '2', 
  //             product_name: 'Gin', 
  //             quantity: 3,
  //             available: 3 // All available
  //           }
  //         ], 
  //         status: 'pending', 
  //         date: '2023-05-15T10:30:00',
  //         notes: 'For weekend stock'
  //       },
  //       { 
  //         id: 'REQ-002', 
  //         requested_by: 'Pool Bar', 
  //         items: [
  //           { 
  //             id: '2-1', 
  //             product_id: '3', 
  //             product_name: 'Whiskey', 
  //             quantity: 2,
  //             available: 0 // None available
  //           }
  //         ],
  //         status: 'pending', 
  //         date: '2023-05-15T09:15:00' 
  //       },
  //       { 
  //         id: 'REQ-003', 
  //         requested_by: 'Lounge Bar', 
  //         items: [
  //           { 
  //             id: '3-1', 
  //             product_id: '4', 
  //             product_name: 'Rum', 
  //             quantity: 4,
  //             available: 4
  //           },
  //           { 
  //             id: '3-2', 
  //             product_id: '5', 
  //             product_name: 'Tequila', 
  //             quantity: 2,
  //             available: 2
  //           }
  //         ],
  //         status: 'pending', 
  //         date: '2023-05-15T11:45:00'
  //       },
  //     ];
  //     setRequests(mockRequests);
  //   } catch (error) {
  //     setToastMessage('Failed to fetch requests');
  //     setShowToast(true);
  //   } finally {
  //     setLoading(false);
  //   }
  // };




  
  const fetchPendingRequests = async () => {
    try {
      const response = await getPendingStewardRequestsWithItems();
      console.log({response})
      setRequests(response.data.data);
    } catch (error) {
      console.error(error)
      setToastMessage('Failed to fetch your requests');
      setShowToast(true);
    }
  };


  const openTransferDialog = (request: TransferRequest) => {
    // Initialize transfer quantities with available amounts (max requested)
    const quantities: Record<string, number> = {};
    request.items.forEach(item => {
      quantities[item.product_id] = Math.min(item.available, item.quantity);
    });
    
    setTransferQuantities(quantities);
    setCurrentRequest(request);
    setTransferDialogOpen(true);
  };

  const handleQuantityChange = (itemId: string, value: number) => {
    setTransferQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, value) // Ensure we don't get negative numbers
    }));
  };

  const handleTransfer = async () => {
    if (!currentRequest) return;

   
    
    setLoading(true);
    setTransferDialogOpen(false);

    const payload = {
      bar_request_id: currentRequest?.request_id,
      requested_by: currentRequest?.requested_by_id,
      items: currentRequest.items.map((item) => ({
        product_id: item.product_id,
        quantity:transferQuantities[item.product_id]
      })),
  };

    try {
      // Simulate API call
      const response = await approveStewardRequest(payload);
      console.log(response)
      

    
       fetchPendingRequests();
      
      setToastMessage('Transfer processed successfully!');
    } catch (error) {
      setToastMessage('Error processing transfer');
    } finally {
      setLoading(false);
      setShowToast(true);
      setCurrentRequest(null);
      setTransferQuantities({});
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle fontSize="small" />;
      case 'partial': return <Warning fontSize="small" />;
      default: return <Pending fontSize="small" />;
    }
  };

  const getItemStatus = (item: RequestItem) => {
    if (item.available === 0) return 'Out of stock';
    if (item.available >= item.quantity) return 'Available';
    return 'Low stock';
  };

  const getItemStatusColor = (item: RequestItem) => {
    if (item.available === 0) return 'error';
    if (item.available >= item.quantity) return 'success';
    return 'warning';
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Bar-Steward Transfers</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Transfer Requests
              </Typography>

                 

              <Button 
                variant="outlined" 
                startIcon={<Inventory />}
                onClick={fetchPendingRequests}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Request ID</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {loading ? <CircularProgress size={24} /> : 'No transfer requests found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests?.map((request,i) => (
                      <TableRow key={i}>
                        <TableCell>{i+1}</TableCell>
                        <TableCell>{request.requested_by}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${request.items.length} items`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.created_at)}</TableCell>
                        <TableCell>
                          <StatusChip 
                            label={request.status} 
                            className={request.status}
                            icon={getStatusIcon(request.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="View items">
                              <IconButton
                                size="small"
                                onClick={() => openTransferDialog(request)}
                                color="primary"
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => openTransferDialog(request)}
                              disabled={request.status !== 'pending' || request.items.every(item => item.available === 0)}
                              startIcon={<CheckCircle />}
                            >
                              Transfer
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>

        {/* Transfer Dialog */}
        <Dialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {/* {console.log({currentRequest})} */}
            {currentRequest ? `Transfer Request ` : 'Transfer Items'}
          </DialogTitle>
          <DialogContent dividers>
            {currentRequest && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">
                    <strong>Requested by:</strong> {currentRequest.requested_by}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Date:</strong> {formatDate(currentRequest.created_at)}
                  </Typography>
                  {currentRequest.notes && (
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                      <strong>Notes:</strong> {currentRequest.notes}
                    </Typography>
                  )}
                </Box>

                {currentRequest.items.some(item => item.available < item.quantity) && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Some items have insufficient stock available
                  </Alert>
                )}
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Requested</TableCell>
                        <TableCell align="right">Available</TableCell>
                        <TableCell align="right">Status</TableCell>
                        <TableCell align="right">Transfer Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentRequest.items.map(item => (
                        <TableRow key={item.product_id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{item.available}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={getItemStatus(item)}
                              size="small"
                              color={getItemStatusColor(item)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={transferQuantities[item.product_id] || 0}
                              onChange={(e) => handleQuantityChange(
                                item.product_id, 
                                parseInt(e.target.value) || 0
                              )}
                              inputProps={{ 
                                min: 0,
                                max: item.quantity
                              }}
                              sx={{ width: 100 }}
                              disabled={item.available === 0}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleTransfer}
              variant="contained"
              color="primary"
              disabled={
                !currentRequest || 
                currentRequest.items.every(item => (transferQuantities[item.product_id] || 0) === 0)
              }
              startIcon={<CheckCircle />}
            >
              Confirm Transfer
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message={'Processing...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
        />
      </IonContent>
    </IonPage>
  );
};

export default StewardPurchaseOrderRequestPage;